import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BreadcrumbService} from 'xng-breadcrumb';
import {Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {Observable, Subject, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError} from 'rxjs/operators';
import {TokenStorageService} from '../../_services/token-storage.service';
import {FileUploadService} from '../../add-item/file-upload.service';
import {
  applyAddListingTaxonomyBreadcrumbs,
  canonicalAddListingRouterUrl,
} from '../../shared/listing-route.utils';
import {AttributeDefinitionView, AttributeSchemaService} from '../../shared/attribute-schema.service';
import {AttributeFormComponent} from '../../shared/attribute-form/attribute-form.component';
import {AppConfigService} from '../../shared/app-config.service';
import {ProfileFlashService} from '../../profile/profile-flash.service';
import {MapsAdminService, PlaceSuggestion} from '../../admin/maps-admin.service';

/** Category-agnostic add-listing form (title, price, location, schema-driven attributes, photos). */
@Component({
  standalone: false,
  selector: 'app-add-listing-form',
  templateUrl: './add-listing-form.component.html',
  styleUrls: ['../../shared/listing-form-styles/listing-form-layout.css', '../../shared/listing-form-styles/listing-form-checkbox.scss']
})
export class AddListingFormComponent implements OnInit, OnDestroy {
  selectedFiles?: FileList;
  /** All files chosen across multiple picker sessions (previews stay in sync with this). */
  selectedFilesList: File[] = [];
  selectedFileNames: any[] = [];
  progressInfos: any[] = [];
  message: string[] = [];
  previews: string[] = [];
  imageInfos?: Observable<any>;

  url: any;
  imageSrc: any;
  counties: any;
  countiesArray: Array<any> = new Array();

  step7: any;
  step8: any;

  myItem: any;
  currentUser: any;
  listingDetailsForm: FormGroup;
  urls: any;
  attributeSchema: AttributeDefinitionView[] = [];
  attributeValues: Record<string, unknown> = {};
  attributesEnabled = true;
  moderationRequired = true;
  placesEnabled = false;
  placesDefaultCountry = '';
  placeQuery = new FormControl('');
  placeSuggestions: PlaceSuggestion[] = [];
  selectedPlace: PlaceSuggestion | null = null;
  schemaError: string | null = null;
  validationError: string | null = null;
  publishError: string | null = null;
  publishMessage: string | null = null;
  publishing = false;
  planLimitHit = false;

  private destroy$ = new Subject<void>();

  @ViewChild(AttributeFormComponent) attributeForm?: AttributeFormComponent;

  constructor(private token: TokenStorageService,
              private router: Router,
              private uploadService: FileUploadService,
              private attributeSchemaService: AttributeSchemaService,
              private appConfigService: AppConfigService,
              private mapsAdmin: MapsAdminService,
              private profileFlash: ProfileFlashService,
              private breadcrumbService: BreadcrumbService,
              private formBuilder: FormBuilder) {
    environment.then(environment => {
      this.url = environment['backendURL'];
      this.imageSrc = environment['images'];
    });
  }

  ngOnInit(): void {
    const canonical = canonicalAddListingRouterUrl(this.router.url);
    if (canonical) {
      void this.router.navigateByUrl(canonical, {replaceUrl: true});
      return;
    }

    this.urls = this.router.url.split('/').filter(element => element);

    this.listingDetailsForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      value: ['', [Validators.required]],
      location: ['', [Validators.required]],
      description: ['', [Validators.required]]
    });

    applyAddListingTaxonomyBreadcrumbs(this.breadcrumbService, this.router.url);

    this.currentUser = this.token.getUser();
    this.step7 = true;
    this.step8 = false;

    this.appConfigService.getConfig().subscribe(config => {
      this.attributesEnabled = config.attributesEnabled;
      this.moderationRequired = config.moderationRequired;
      this.placesEnabled = !!config.placesEnabled;
      this.placesDefaultCountry = config.placesDefaultCountry || '';
      if (config.attributesEnabled) {
        this.loadAttributeSchema();
      }
      if (this.placesEnabled) {
        this.wirePlacesAutocomplete();
      } else {
        this.loadCounties();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCounties(): void {
    this.uploadService.getCounties(this.url + '/counties')
      .subscribe(response => {
        this.counties = response;
        this.counties.forEach(obj => {
          if (!this.countiesArray.includes(obj)) {
            this.countiesArray.push(obj);
          }
        });
      });
  }

  private wirePlacesAutocomplete(): void {
    this.placeQuery.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        const query = (q || '').trim();
        if (query.length < 2) {
          return of([] as PlaceSuggestion[]);
        }
        return this.mapsAdmin.autocomplete(query, this.placesDefaultCountry || undefined).pipe(
          catchError(() => of([] as PlaceSuggestion[]))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.placeSuggestions = results || [];
    });
  }

  onPlaceSelected(place: PlaceSuggestion): void {
    this.selectedPlace = place;
    const locationValue = place.city || place.region || place.label;
    this.listingDetailsForm.patchValue({location: locationValue});
    this.placeQuery.setValue(place.label || locationValue, {emitEvent: false});
    this.placeSuggestions = [];
  }

  displayPlace(place: PlaceSuggestion | string | null): string {
    if (!place) {
      return '';
    }
    if (typeof place === 'string') {
      return place;
    }
    return place.label || place.city || '';
  }

  goToStep7() {
    this.step7 = true;
    this.step8 = false;
    setTimeout(() => {
      document.getElementById('step7')?.scrollIntoView({behavior: 'smooth'});
    }, 100);
  }

  goToStep8() {
    if (!this.validateListingForms()) {
      return;
    }
    this.step8 = true;
    this.step7 = false;
    setTimeout(() => {
      document.getElementById('step8')?.scrollIntoView({behavior: 'smooth'});
    }, 100);
  }

  selectFiles(event: any): void {
    this.message = [];
    this.progressInfos = [];
    this.selectedFiles = event.target.files;

    if (this.selectedFiles && this.selectedFiles[0]) {
      const maxImages = 10;
      const remainingSlots = maxImages - this.previews.length;

      if (remainingSlots <= 0) {
        alert('Maximum 10 images allowed.');
        return;
      }

      const numberOfFiles = Math.min(this.selectedFiles.length, remainingSlots);
      if (this.selectedFiles.length > remainingSlots) {
        alert(`Only ${remainingSlots} more images can be added.`);
      }

      for (let i = 0; i < numberOfFiles; i++) {
        const file = this.selectedFiles[i];
        this.selectedFilesList.push(file);
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.previews.push(e.target.result);
        };
        reader.readAsDataURL(file);
        this.selectedFileNames.push(file.name);
      }
    }

    event.target.value = '';
  }

  uploadFiles(): void {
    this.message = [];
    if (this.selectedFiles) {
      for (let i = 0; i < this.selectedFiles.length; i++) {
        this.upload(i, this.selectedFiles[i]);
      }
    }
  }

  upload(idx: number, file: File): void {
    this.progressInfos[idx] = {value: 0, fileName: file.name};

    if (file) {
      this.uploadService.upload(file, this.url, this.myItem.itemId).subscribe(
        (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.progressInfos[idx].value = Math.round((100 * event.loaded) / event.total);
          } else if (event instanceof HttpResponse) {
            this.message.push(file.name + ': Successful!');
            this.imageInfos = this.uploadService.getFiles(this.url, this.myItem.itemId);
          }
        },
        (err: any) => {
          this.progressInfos[idx].value = 0;
          let msg = file.name + ': Failed!';
          if (err.error && err.error.message) {
            msg += ' ' + err.error.message;
          }
          this.message.push(msg);
        }
      );
    }
  }

  removeImage(index: any): void {
    this.previews.splice(index, 1);
    this.selectedFileNames.splice(index, 1);
    this.selectedFilesList.splice(index, 1);
  }

  private loadAttributeSchema(): void {
    const categoryPath = this.attributeSchemaService.categoryPathFromUrlSegments(this.urls);
    this.attributeSchemaService.getSchema(categoryPath).subscribe({
      next: schema => {
        this.attributeSchema = schema?.attributes || [];
        this.schemaError = null;
      },
      error: () => {
        this.attributeSchema = [];
        this.schemaError = 'Unable to load category attribute schema.';
      }
    });
  }

  onAttributeValuesChange(values: Record<string, unknown>): void {
    this.attributeValues = values || {};
  }

  private validateListingForms(): boolean {
    this.validationError = null;
    this.listingDetailsForm.markAllAsTouched();
    const listingValid = this.listingDetailsForm.valid;
    const hasAttributeFields = this.attributeForm?.sectionGroups?.length;
    const attributesValid = !this.attributesEnabled
      || !hasAttributeFields
      || (this.attributeForm?.validate() ?? true);
    if (!listingValid || !attributesValid) {
      this.validationError = 'Please complete all required fields.';
      setTimeout(() => {
        document.querySelector('.schema-error, .mat-mdc-form-field-invalid, .field-invalid')
          ?.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 100);
      return false;
    }
    return true;
  }

  private buildListingData() {
    const location = this.listingDetailsForm.get('location')?.value;
    const listingDetails: Record<string, unknown> = {
      name: this.listingDetailsForm.get('name')?.value,
      value: this.listingDetailsForm.get('value')?.value,
      location,
      description: this.listingDetailsForm.get('description')?.value,
      url: this.urls
    };
    if (this.selectedPlace) {
      listingDetails.place = {
        label: this.selectedPlace.label,
        city: this.selectedPlace.city,
        region: this.selectedPlace.region,
        country: this.selectedPlace.country,
        lat: this.selectedPlace.lat,
        lng: this.selectedPlace.lng,
        placeId: this.selectedPlace.placeId
      };
    }
    return {
      listingDetails,
      attributes: this.attributesEnabled
        ? (this.attributeForm?.collectValues() ?? this.attributeValues)
        : {},
      images: {
        fileNames: this.selectedFileNames,
        previewCount: this.previews.length
      },
      userId: this.currentUser?.username,
      timestamp: new Date().toISOString()
    };
  }

  publishWithPhotos() {
    if (!this.validateListingForms()) {
      this.goToStep7();
      return;
    }
    const formData = new FormData();
    formData.append('itemData', JSON.stringify(this.buildListingData()));

    for (const file of this.selectedFilesList) {
      formData.append('images', file);
    }

    this.submitListing(formData);
  }

  publishWithoutPhotos() {
    if (!this.validateListingForms()) {
      this.goToStep7();
      return;
    }
    const formData = new FormData();
    formData.append('itemData', JSON.stringify(this.buildListingData()));

    this.submitListing(formData);
  }

  get primarySubmitLabel(): string {
    return this.moderationRequired ? 'Submit for review' : 'Publish listing';
  }

  get skipPhotosLabel(): string {
    return this.moderationRequired ? 'Submit without photos' : 'Publish without photos';
  }

  private submitListing(formData: FormData): void {
    this.publishing = true;
    this.publishError = null;
    this.publishMessage = null;
    this.planLimitHit = false;
    this.uploadService.publishItemListing(this.url, formData).subscribe({
      next: response => {
        this.publishing = false;
        const status = String(response?.status || (this.moderationRequired ? 'PENDING' : 'PUBLISHED')).toUpperCase();
        const message = status === 'PENDING'
          ? 'Listing submitted for review. You can track it below.'
          : 'Listing is live on the marketplace.';
        this.profileFlash.setListingMessage(message);
        void this.router.navigate(['/profile']);
      },
      error: error => {
        this.publishing = false;
        if (error?.status === 402 || error?.error?.code === 'PLAN_LIMIT') {
          this.publishError = error?.error?.error
            || 'Listing limit reached. Upgrade your plan in Profile → Settings.';
          this.planLimitHit = true;
          return;
        }
        this.planLimitHit = false;
        const body = error?.error;
        this.publishError = body?.error || 'Failed to submit listing. Please try again.';
        if (body?.detail && body.detail !== body.error) {
          this.publishError = `${this.publishError} (${body.detail})`;
        }
      }
    });
  }
}
